// React / 기본 라이브러리
import { useEffect, useState, useMemo, useCallback, useRef } from "react";
import { Link } from "react-router-dom";

// geoJSON 타입 관련 외부 라이브러리
import { Feature, FeatureCollection, GeoJsonProperties, Point } from "geojson";

// 공통 유틸 / API / 타입
import { encodeUnicode, path } from "../common/Common";
import { Category, Metadata } from "../../components/common/Common";
import { DataTemplate } from "../map/sidesheet/addplace/AddSinglePlaceInput";
import { validateAddressField } from "./AddressValidator";
import { GeocodingQueryParams } from "../../data_provider/gateways/RequestGateway";
import DataProvider from "../../data_provider/DataProvider";

// 공통 UI 컴포넌트
import { useModal } from "../modal/Modal";
import ModalButtons from "../modal/ModalButtons";
import LoadingSpinner from "../common/LoadingSpinner";

// 지도 제작 단계별 컴포넌트
import CreateMapProcess from "./CreateMapProcess";
import DataImporter from "./DataImporter";
import AddressFieldSetting from "./AddressFieldSetting";
import MarkerSetting from "./MarkerSetting";
import PlaceInfoSetting from "./PlaceInfoSetting";
import MapSetting from "./MapSetting";
import MapSettingComplete from "./MapSettingComplete";
import MapComplete from "./MapComplete";
import DataTable from "./DataTable";

// 스타일
import styles from "./CreateMap.module.css";
import { colorItem } from "./Marker";
import SummaryPrompt from "../ai/SummaryPrompt";

import { MultiFullGeocodingReqBody } from "../../data_provider/gateways/RequestGateway";
import { feature } from "@turf/turf";
import { GeocodingStats, aggregateGeocodingStats, getFailedGeocodingResults, GeocodingResultItem, getFailedQueriesFromFailedResults } from "../../utils/geocodingUtils";

const alphabet = ["A", "B", "C", "D", "E", "F", "G", "H", "I", "J", "K", "L", "M", "N", "O", "P", "Q", "R", "S", "T", "U", "W", "X", "Y", "Z"];

interface SampleMapData {
    row1: string[];
    row2: string[];
}

interface OptionByCategory {
    optionByCategory: {
        [fieldName: string]: string[];
    };
}

interface CRS {
    type: "name";
    properties: {
        code: string;
    };
}

export interface MainImageType {
    isMainImage: boolean;
    mainImageIdx: number;
    mainimageUrl?: string;
}

export const defaultStyle = {
    color: "#666",
    fontStyle: "",
    fontWeight: "",
    textAlign: "left",
    textDecoration: "",
};

export interface markerInfoType {
    markerInfo: {
        markerColor: string;
        marker: string;
    }[];
    fieldNameIdx: number;
    selectColorIdx: number;
    categoryOptionIdx: number;
    groupIdx: number;
}

export interface MainDataType {
    fieldName: string;
    fieldValue: string;
    template?: DataTemplate;
    style?: {
        color: string;
        fontStyle?: string;
        fontWeight?: string;
        textAlign?: string;
        textDecoration?: string;
    };
}

export interface Content extends MainDataType {
    template: DataTemplate;
    style: {
        color: string;
        fontStyle: string;
        fontWeight: string;
        textAlign: string;
        textDecoration: string;
    };
}

export interface MapSaveData {
    mapType: string;
    private: number;
    markerInfo: {
        filterKey: string;
        filterStyles:
            | {
                  filterValue: string;
                  styleString: {
                      marker: string;
                      markerColor: string;
                  };
              }[]
            | [];
        label: string;
    };
    placeInfo: string;
    platform: number;
    infoStyle: string;
    metadataField: Metadata;
    geojson: string;
    category: Category[];
}
// TO DO
// DataTable click 시 TextArea로 변경하고 다시 눌렀을때 DataTable로 변경하는 로직 필요(클릭 시 수정기능 안하기로 함)
/**
 * @author sukim91, cjs, YoungjunKim, smy
 * @date 2025-04-10
 * @remark 지도만들기 컴포넌트
 * @props None
 * @returns CreateMap
 */
export function CreateMap() {
    // ----------------
    // -   useState   -
    // ----------------

    const [processIdx, setProcessIdx] = useState<number>(0); // 지도 제작 기능 프로세스 인덱스 0~5
    const containerRef = useRef<HTMLDivElement>(null); // 컨테이너 스크롤 참조

    // SummaryPrompt 관련 state
    const [showSummaryPrompt, setShowSummaryPrompt] = useState<boolean>(false);
    const [summaryData, setSummaryData] = useState<string>("");
    const [summaryCompleted, setSummaryCompleted] = useState<boolean>(false);
    const [summaryRequested, setSummaryRequested] = useState<boolean>(false);

    const [isData, setIsData] = useState<boolean>(false); // 엑셀 파일에 주소 데이터 유무 파악
    const [initMapData, setInitMapData] = useState<string[][]>(); // 엑셀 기반 지도 정보

    const [sampleData, setSampleData] = useState<SampleMapData>({ row1: [], row2: [] }); // 주소 추론 샘플 데이터
    const [addressFieldIdx, setAddressFieldIdx] = useState<number>(0); // 주소 필드 인덱스
    const [addressFieldMapdata, setAddressFieldMapdata] = useState<string[][]>(); // 주소 필드 데이터
    const [inferredAddressFieldIndices, setInferredAddressFieldIndices] = useState<number[] | undefined>(); // 추론된 주소필드 인덱스 리스트
    const [selectedAddressIdx, setSelectedAddressIdx] = useState<number>(0); // 선택된 주소 인덱스(필드 인덱스 아님)
    const [mainImage, setMainImage] = useState<MainImageType>({ isMainImage: false, mainImageIdx: 0 });

    // 장소 정보 및 Style
    const [mapSettingData, setMapSettingData] = useState({
        placeInfo: {
            title: "",
            contents: [
                {
                    fieldName: "",
                    fieldValue: "",
                    template: DataTemplate.none,
                    style: { ...defaultStyle },
                },
            ],
            selectedId: 1,
        },
    });

    const [optionByCategory, setOptionByCategory] = useState<OptionByCategory>({
        optionByCategory: {},
    });

    const [markerInfo, setMarkerInfo] = useState<markerInfoType>({
        markerInfo: [],
        fieldNameIdx: 1,
        selectColorIdx: 0,
        categoryOptionIdx: 0,
        groupIdx: 0,
    });

    // 25.07.18 | Kim Young Jun | 필드 중복 검사 상태
    const [isFieldConflict, setIsFieldConflict] = useState<boolean>(false);
    // 25.07.18 | Kim Young Jun | 주소 필드 & 칼럼 수 등 지도 데이터 임포트 실패 상태
    const [isNotValidMapData, setIsNotValidMapData] = useState<boolean>(false);

    const [failMapData, setFailMapData] = useState<string[][]>([]);
    const [geojsonDataFeatures, setGeojsonDataFeatures] = useState<Feature[]>([]);
    const [mapSaveData, setMapSaveData] = useState<MapSaveData>();

    const [geoCodingProgress, setGeoCodingProgress] = useState<number>(0);
    const [mapComplete, setMapComplete] = useState(false);

    const [geocodingStats, setGeocodingStats] = useState<GeocodingStats>({
        totalCnt: 0,
        exactCnt: 0,
        similarCnt: 0,
        failCnt: 0,
        matchingRate: 0,
		failedAddresses: []
        // geocodingProcessingTime: 0
    });

    const [geocodingStartTime, setGeocodingStartTime] = useState<number>(0);

    const [mapType, setMapType] = useState("baroEmap");

    const [canProceed, setCanProceed] = useState(false);

    const { ModalComponent, openModal, closeModal } = useModal();
    const [isLoading, setIsLoading] = useState(false);

    const [isUserDataEditMode, setIsUserDataEditMode] = useState<boolean>(false);
    const _failMapData: string[][] = [];

    // ---------------
    // -   useMemo   -
    // ---------------

    const geojsonString = useMemo(() => {
        if (!mapComplete) return "";

        const _geojsonString = JSON.stringify(buildGeoJSON(geojsonDataFeatures));

        return _geojsonString;
    }, [mapComplete]);

    // -----------------
    // -   useEffect   -
    // -----------------

    // processIdx 의존 Effect
    useEffect(() => {
        if (!initMapData) return;

        if (processIdx === 5) {
            const metadataField = createMetadataField();
            const category = createCategory();
            const infoStyle = createInfoStyle();
            const stringfiedGeojson = JSON.stringify(buildGeoJSON(geojsonDataFeatures));

            // 251125 | smyun | 카테고리 없음(선택안함) 추가
            // 카테고리 필드명이 존재하고 실제로 카테고리가 선택된 경우만 필터 정보 생성
            const categorySelected =
                markerInfo.categoryOptionIdx !== 0 && Object.keys(optionByCategory.optionByCategory).length > markerInfo.categoryOptionIdx;
            let markerInfoData: MapSaveData["markerInfo"];

            if (categorySelected) {
                const categoryFieldName = Object.keys(optionByCategory.optionByCategory)[markerInfo.categoryOptionIdx];
                const filterKey = alphabet[initMapData[0].findIndex((name) => name === categoryFieldName)];
                const filterStyles = (optionByCategory.optionByCategory[categoryFieldName] || []).map((val, idx) => ({
                    filterValue: val,
                    styleString: {
                        marker: markerInfo.markerInfo[idx]?.marker ?? "markerSelect1",
                        markerColor: markerInfo.markerInfo[idx]?.markerColor ?? colorItem[idx % colorItem.length],
                    },
                }));

                markerInfoData = {
                    filterKey,
                    label: alphabet[markerInfo.fieldNameIdx],
                    filterStyles,
                };
            } else {
                // 카테고리 없음: filterKey/ filterStyles 비워둠
                // label은 카테고리여부와 관계 없이 동일하게 전달
                markerInfoData = {
                    filterKey: "",
                    label: alphabet[markerInfo.fieldNameIdx],
                    filterStyles: [],
                };
            }

            const data: MapSaveData = {
                mapType,
                private: 0,
                markerInfo: markerInfoData,
                placeInfo: encodeUnicode(JSON.stringify(mapSettingData.placeInfo.contents)),
                platform: 1,
                geojson: stringfiedGeojson,
                metadataField,
                category,
                infoStyle,
            };

            setMapSaveData(data);
        }
    }, [processIdx]);

    // 임포트 받은 최초 지도 데이터(initMapData) 의존 Effect
    useEffect(() => {
        if (!initMapData) return;

        if (initMapData.length > 1) {
            setSampleData({ row1: initMapData[0], row2: initMapData[1] });
        } else if (initMapData.length === 1) {
            setSampleData({ row1: initMapData[0], row2: [] });
        }

        const fetchAddressValidateResult = async () => {
            await inferAddressField();
        };

        fetchAddressValidateResult();

        let contents: Content[] = [];

        for (let idx = 0; idx < 5; idx++) {
            const fieldValue = idx === 0 ? "" : "${fieldValue}";
            const content = {
                fieldName: initMapData[0][idx],
                fieldValue,
                template: DataTemplate.none,
                style: { ...defaultStyle },
            };
            contents.push(content);
        }

        const _mapSettingData = { ...mapSettingData };

        _mapSettingData.placeInfo.contents = contents;
        _mapSettingData.placeInfo.title = initMapData[1][0];

        setMapSettingData(_mapSettingData);
        initCategoryOption();
    }, [initMapData]);

    // 주소 필드 변경 의존 Effect
    useEffect(() => {
        handleAddressField();
    }, [selectedAddressIdx]);

    // 25.07.18 | Kim Young Jun | markerInfo 변경 시 중복 검사 로직
    useEffect(() => {
        const { categoryOptionIdx, fieldNameIdx } = markerInfo;

        // 카테고리 인덱스와 장소명 인덱스가 같아진 경우
        // 251125 | smyun | 카테고리Option의 0번째에 "선택안함"강제 추가로 index에 -1처리
        if (categoryOptionIdx - 1 === fieldNameIdx) {
            setIsFieldConflict(true);
            openModal({
                title: "경고",
                children: (
                    <>
                        <p>
                            <b>'카테고리'와 '장소이름'은 같을 수 없습니다.</b>
                        </p>
                        <p>다른 필드를 선택해주세요.</p>
                    </>
                ),
                modalButtons: (
                    <ModalButtons
                        buttonCount={1}
                        buttons={[
                            {
                                label: "확인",
                                color: "Primary",
                                onClick: () => {
                                    closeModal();
                                },
                            },
                        ]}
                    />
                ),
            });
        } else {
            setIsFieldConflict(false);
        }
    }, [markerInfo.categoryOptionIdx, markerInfo.fieldNameIdx, openModal, closeModal]);

    // 25.07.18 | Kim Young Jun | canProceed 상태 업데이트 (모든 검사 조건 확인)
    useEffect(() => {
        // 주소 필드 설정 단계: 주소 필드를 찾지 못한 경우 진행 불가
        if (processIdx === 0) {
            setCanProceed(!isNotValidMapData);
        }
        // 마커설정 단계: 필드 중복 및 카테고리 그룹 개수 초과시 진행 불가
        else if (processIdx === 1) {
            // 1. 필드 중복 검사
            const hasFieldConflict = isFieldConflict;

            // 2. 그룹 제한 검사 (16개 초과)
            const fieldName = Object.keys(optionByCategory.optionByCategory)[markerInfo.categoryOptionIdx];
            const selectedGroupValues = optionByCategory.optionByCategory[fieldName] || [];
            const hasGroupLimitExceeded = selectedGroupValues.length > 16;

            // 두 조건 중 하나라도 문제가 있으면 진행 불가
            setCanProceed(!hasFieldConflict && !hasGroupLimitExceeded);
        }
    }, [processIdx, isFieldConflict, isNotValidMapData, optionByCategory, markerInfo.categoryOptionIdx]);

    // SummaryPrompt 표시 로직 - processIdx 0에서 지도 데이터가 로드된 후
    useEffect(() => {
        if (initMapData && processIdx === 0 && initMapData.length > 1 && !summaryRequested) {
            // 데이터가 로드되었고 첫 번째 단계일 때, 아직 요약을 요청하지 않았다면 SummaryPrompt 표시
            setShowSummaryPrompt(true);
            setSummaryRequested(true);
        } else if (processIdx === 0 && summaryRequested) {
            // processIdx가 0이고 이미 요약을 요청했다면 SummaryPrompt 다시 표시
            setShowSummaryPrompt(true);
        } else if (processIdx !== 0) {
            // processIdx가 0이 아니면 SummaryPrompt 숨김
            setShowSummaryPrompt(false);
        }
    }, [initMapData, processIdx, summaryRequested]);

    // 25.09.22 | Kim Young Jun | Container 렌더링 완료 후 스크롤 처리 (drag&drop 시)
    useEffect(() => {
        if (initMapData && addressFieldMapdata && isData && processIdx === 0 && containerRef.current) {
            // Container가 실제로 렌더링된 후 스크롤 실행
            setTimeout(() => {
                if (containerRef.current) {
                    containerRef.current.scrollIntoView({
                        behavior: "smooth",
                        block: "start",
                    });
                }
            }, 150); // 조금 더 긴 지연으로 확실한 렌더링 완료 대기
        }
    }, [initMapData, addressFieldMapdata, isData, processIdx]);

    // -------------------
    // -   useCallBack   -
    // -------------------

    const progressProcess = useCallback((delta: number) => {
        setProcessIdx((prev) => Math.max(0, Math.min(5, prev + delta)));
    }, []);

    // ----------------------
    // -   customFunction   -
    // ----------------------

    // AddressValidator.ts의 validateAddressField 함수를 사용해 주소필드 추론 결과 확인
    const inferAddressField = async () => {
        if (!initMapData) return;

        openModal({
            title: "주소 칼럼을 판별중입니다...",
            children: <LoadingSpinner />,
        });
        setIsLoading(true);

        try {
            let isValiated = true;
            const result = await validateAddressField(initMapData);

            // 25.07.18 | Kim Young Jun | 주소필드 결과가 없거나 칼럼이 1개만 있을 때
            if (result.length === 0 || initMapData[0].length <= 1) {
                setIsNotValidMapData(true); // 지도 데이터 임포트 실패 상태 설정

                let modalMessage = "";
                if (initMapData[0].length <= 1) {
                    modalMessage = "지도 제작을 위해서는 최소 2개 이상의 칼럼이 필요합니다.";
                } else {
                    modalMessage = "주소로 추정되는 칼럼이 없습니다.";
                }

                openModal({
                    title: "데이터 임포트 결과",
                    children: modalMessage,
                    modalButtons: (
                        <ModalButtons
                            buttonCount={1}
                            buttons={[
                                {
                                    label: "확인",
                                    color: "Primary",
                                    onClick: () => {
                                        closeModal();
                                    },
                                },
                                {
                                    label: "데이터 다시 추가",
                                    color: "Primary",
                                    onClick: () => {
                                        initialize();
                                        closeModal();
                                    },
                                },
                                {
                                    label: "데이터 직접 편집",
                                    color: "Primary",
                                    onClick: () => {
                                        setIsUserDataEditMode(true);
                                        closeModal();
                                    },
                                },
                            ]}
                        />
                    ),
                });
                isValiated = false;
            } else {
                setIsNotValidMapData(false); // 지도 데이터 임포트 실패 상태 해제
            }

            // 주소 컬럼 후보 인덱스 리스트 저장
            setInferredAddressFieldIndices(result);

            // 상위 5개 행 데이터 중, 추정된 주소 컬럼 데이터 저장
            const topRows = initMapData.slice(0, 6);
            const addressFieldData = topRows.map((row) => row.filter((_, colIdx) => result.includes(colIdx)));
            setAddressFieldMapdata(addressFieldData);

            if (isValiated) {
                // 가장 추론값 높은 칼럼명 추출
                const maxIndex = result.length > 0 ? result[0] : -1;
                const selectedColumnName = maxIndex !== -1 ? initMapData[0][maxIndex] : "알 수 없음";

                openModal({
                    title: "안내",
                    children: (
                        <p>
                            가장 가능성 높은 주소 컬럼 '<b>{selectedColumnName}</b>'을(를) 자동 선택했습니다.
                        </p>
                    ),
                    modalButtons: (
                        <ModalButtons
                            buttonCount={1}
                            buttons={[
                                {
                                    label: "확인",
                                    color: "Primary",
                                    onClick: () => {
                                        closeModal();
                                    },
                                },
                            ]}
                        />
                    ),
                });
                setAddressFieldIdx(maxIndex);
            }
        } catch (error) {
            setIsNotValidMapData(true); // 오류 발생 시에도 지도 데이터 임포트 실패로 처리
            openModal({
                title: "안내",
                children: "주소 필드를 판별하는 중 오류가 발생했습니다.",
                modalButtons: (
                    <ModalButtons
                        buttonCount={1}
                        buttons={[
                            {
                                label: "확인",
                                color: "Primary",
                                onClick: () => {
                                    closeModal();
                                },
                            },
                        ]}
                    />
                ),
            });
        } finally {
            setIsLoading(false);
        }
    };

    // 주소 필드 선택변경 시 사용할 주소 필드 변경
    const handleAddressField = () => {
        // 선택된 인덱스를 주소 컬럼 인덱스로 변경 작업
        if (!inferredAddressFieldIndices) return;
        const AddressFieldIdx = inferredAddressFieldIndices[selectedAddressIdx];
        setAddressFieldIdx(AddressFieldIdx);
    };

    // 카테고리 필드 후보 탐색 및 마커 스타일 초기화
    const initCategoryOption = () => {
        if (!initMapData) return;

        let _optionByCategorys: OptionByCategory = { ...optionByCategory };

        for (let i = 0; i < initMapData[0].length; i++) {
            let len = initMapData.length;
            let tmpOptionByCategory: string[] = [];

            for (let j = 1; j < len; j++) {
                if (!tmpOptionByCategory.includes(initMapData[j][i])) {
                    tmpOptionByCategory.push(initMapData[j][i]);
                }

                // 그룹핑 길이가 16를 초과 하면 제외
                // if (tmpOptionByCategory.length > 16) {
                //     flag = false;
                //     break;
                // }
            }

            _optionByCategorys.optionByCategory[initMapData[0][i]] = tmpOptionByCategory;
        }

        // const fieldName = Object.keys(_optionByCategorys.optionByCategory)[0];

        // const markerItems = _optionByCategorys.optionByCategory[fieldName].map((_, idx) => ({
        //     marker: "markerSelect1",
        //     markerColor: colorItem[idx],
        // }));

        // setOptionByCategory(_optionByCategorys);
        // setMarkerInfo({ ...markerInfo, markerInfo: markerItems });
        // selectMarkerLabel(1);

        // 251125 | smyun | 카테고리 선택안함을 0번째에 추가
        const SELECT_NONE_LABEL = "선택안함";
        // 기존 맵의 앞에 '선택안함' 추가 (값은 빈 배열)
        const newOptionMap: { [k: string]: string[] } = {
            [SELECT_NONE_LABEL]: [],
            ..._optionByCategorys.optionByCategory,
        };
        const newOptionByCategory: OptionByCategory = { optionByCategory: newOptionMap };

        // 기본적으로 마커 아이템을 만들어 markerInfo 초기화 (기존 동작 유지하되 '선택안함'은 건너뜀)
        const keys = Object.keys(newOptionByCategory.optionByCategory);
        // keys[0] == '선택안함' 이므로, 실제 기본 카테고리 값은 keys[1] (없으면 keys[0] 사용)
        const defaultCategoryKey = keys.length > 1 ? keys[1] : keys[0];
        const defaultCategoryValues = newOptionByCategory.optionByCategory[defaultCategoryKey] || [];

        const markerItems = defaultCategoryValues.map((_, idx) => ({
            marker: "markerSelect1",
            markerColor: colorItem[idx % colorItem.length],
        }));

        // 저장(상태 갱신)
        setOptionByCategory(newOptionByCategory);
        // markerInfo의 markerInfo 필드는 기본적으로 카테고리값 갯수에 맞춰 채움.
        // (초기 UI 상태가 '선택안함'이면 markerInfo.markerInfo는 빈 상태 -> ensureSingle 처리는 Marker에서 함)
        setMarkerInfo((prev) => ({ ...prev, markerInfo: markerItems }));

        // placeName 선택 라벨(1)을 기본으로 설정
        selectMarkerLabel(1);
    };

    // 지도 카테고리 처리 함수
    const createCategory = (): Category[] => {
        const result: Category[] = [];

        if (!initMapData) return result;

        // 251125 | smyun | 카테고리 선택안함 추가
        // '선택안함'을 index 0으로 두는 UI 규칙을 따름.
        // markerInfo.categoryOptionIdx === 0 이면 "카테고리 없음"으로 간주
        if (markerInfo.categoryOptionIdx === 0) {
            return result; // 빈 배열 반환 -> 카테고리 없음
        }

        const fieldName = Object.keys(optionByCategory.optionByCategory)[markerInfo.categoryOptionIdx];
        const categoryValues = optionByCategory.optionByCategory[fieldName];

        for (let i = 0; i < categoryValues.length; i++) {
            const value = categoryValues[i];
            const style = markerInfo.markerInfo[i];

            result.push({
                icon: style.marker,
                label: value,
                color: style.markerColor,
            });
        }

        return result;
    };

    // 지도 메타데이터 처리 함수
    const createMetadataField = (): Metadata => {
        if (!initMapData) throw new Error("mapdata가 정의되지 않았습니다.");

        const totalColumns = initMapData[0].length;
        const fieldNameMap: { [key: string]: string } = {};

        for (let i = 0; i < totalColumns; i++) {
            fieldNameMap[alphabet[i]] = initMapData[0][i];
        }

        const geoField = alphabet[addressFieldIdx];

        // 251125 | smyun | 카테고리 없음(선택안함) 추가
        // 카테고리 없음 처리: markerInfo.categoryOptionIdx === 0 를 "없음" 신호로 사용, categoryOptionIdx - 1 인덱스 보정
        const category = markerInfo.categoryOptionIdx === 0 ? null : alphabet[markerInfo.categoryOptionIdx - 1];

        // 카테고리 없음일 때 사용할 단일 마커/색상 값 (UI에서 selectColorIdx로 선택한다고 가정)
        let marker: string | null = null;
        let markerColor: string | null = null;
        if (category === null) {
            const selIdx = Math.max(0, Math.min(markerInfo.selectColorIdx ?? 0, (markerInfo.markerInfo || []).length - 1));
            if (markerInfo.markerInfo && markerInfo.markerInfo[selIdx]) {
                marker = markerInfo.markerInfo[selIdx].marker;
                markerColor = markerInfo.markerInfo[selIdx].markerColor;
            } else {
                // 안전 널값: 기본값 제공(필요 시 변경)
                marker = "markerSelect2";
                markerColor = colorItem[0];
            }
        }
        const placeName = alphabet[markerInfo.fieldNameIdx];

        const imageField = mainImage.isMainImage ? alphabet[mainImage.mainImageIdx] : null;

        if (!fieldNameMap[placeName] || fieldNameMap[placeName].trim() === "") {
            throw new Error(`[placeName: ${placeName}] 에 해당하는 필드명이 fieldNameMap에 존재하지 않거나 빈 값입니다.`);
        }

        const mainData: string[] = [];
        const extraData: string[] = [];

        mapSettingData.placeInfo.contents.forEach((content, _) => {
            const colIdx = initMapData[0].findIndex((name) => name === content.fieldName);

            if (colIdx !== -1) {
                const key = alphabet[colIdx];
                mainData.push(key);
            }
        });

        // category가 있을 때는 marker/markerColor 키를 아예 포함시키지 않음
        let metaData: Metadata = {
            geoField,
            category,
            placeName,
            mainData,
            extraData,
            fieldName: fieldNameMap,
            imageField: imageField,
        };

        if (category === null) {
            metaData.marker = marker;
            metaData.markerColor = markerColor;
        }

        return metaData;
    };

    //  InfoStyle 데이터 처리 함수
    const createInfoStyle = (): string => {
        if (!initMapData) throw new Error("mapData가 정의되지 않았습니다.");

        const contents = mapSettingData.placeInfo.contents;

        // 1. InfoStyle 형태 구성
        const infoStyle = {
            placeName: initMapData[0][markerInfo.fieldNameIdx], // 컬럼명 그대로
            // 장소설정 시 '선택안함'컬럼이 추가되어 idx가 1부터 시작하므로, 0번 컬럼을 가져오지 않도록 1부터 시작하도록 처리 | 250828 | smyun
            field: contents.slice(1).map((content, _) => {
                const colIdx = initMapData[0].findIndex((name) => name === content.fieldName);
                const fieldKey = colIdx !== -1 ? alphabet[colIdx] : "";

                // fieldName 기준으로 contents에서 매칭된 편집값 찾아오기
                // const matched = contents.find((c) => c.fieldName === content.fieldName);
                // const fieldValue = matched?.fieldValue ?? "${value}";

                return {
                    fieldName: content.fieldName,
                    fieldKey,
                    // TextEditor에서 편집한 값을 value에 반영 | 250827 | smyun
                    value: content.fieldValue || "${value}",
                    template: content.template,
                    style: JSON.stringify(content.style),
                };
            }),
        };

        // console.log("[DBG]infostyle", infoStyle);

        // 2. UTF-8 Safe JSON String
        return encodeUnicode(JSON.stringify(infoStyle));
    };

    const runGeocoding = () => {
        initMapData && progress(initMapData); // 원본 데이터 주소로 지오코딩
    };

    function buildGeoJSON(features: Feature[], epsg: string = "5179"): FeatureCollection & { crs: CRS } {
        return {
            type: "FeatureCollection",
            features,
            crs: {
                type: "name",
                properties: {
                    code: `${epsg}`,
                },
            },
        };
    }

    // const buildGeoJSON = (
    //     features: Feature[],
    //     mapType: "baroEmap" | "OSM" | "uMap"
    // ): FeatureCollection & { crs: CRS } => {
    //     let epsg: string;

    //     switch (mapType) {
    //         case "baroEmap":
    //             epsg = "5179"; // UTM-K (한국 전용)
    //             break;
    //         case "OSM":
    //         case "uMap":
    //             epsg = "3857"; // Web Mercator
    //             break;
    //         default:
    //             epsg = "4326"; // Fallback: WGS84
    //     }

    //     return {
    //         type: "FeatureCollection",
    //         features,
    //         crs: {
    //             type: "name",
    //             properties: {
    //                 code: epsg,
    //             },
    //         },
    //     };
    // };

    const progress = (data: string[][]) => {
        const resultFeature: Feature[] = [];
        const startTime = Date.now(); // 로컬 변수로 시작 시간 저장

        // Geocoding 통계 초기화
        setGeocodingStats({
            totalCnt: 0,
            exactCnt: 0,
            similarCnt: 0,
            failCnt: 0,
            matchingRate: 0,
            // geocodingProcessingTime: 0
        });

        _failMapData.push(data[0]);

        const max = data ? data.length - 1 : 0;

        const process = async () => {
            const batchSize = 50;
            for (let i = 1; i <= max; i += batchSize) {
                let j = 0;
                const indexs = [];
                const end = i + batchSize;
                for (j = i; j < end; j++) {
                    if (j > max) {
                        break;
                    }

                    const row = data[j];
                    if (row && row[addressFieldIdx] != null) {
                        indexs.push(j);
                    }
                }

                if (indexs.length !== 0) {
                    const features = await multiFullGeoCoding(data, indexs);
                    if (features) {
                        for (let k = 0; k < features.length; ++k) {
                            const feature = features[k];
                            if (feature) {
                                resultFeature.push(feature);
                            } else {
                                _failMapData.push(data[j]);
                            }
                        }
                    }
                } else {
                    i = max;
                }

                setGeoCodingProgress(Math.ceil((i / max) * 100));
                await new Promise((res) => setTimeout(res, 20));
            }

            setGeojsonDataFeatures([...resultFeature]);

            // 처리시간 계산 및 저장
            const endTime = Date.now();
            const processingTimeInSeconds = Math.round(((endTime - startTime) / 1000) * 10) / 10; // 소수점 첫째자리까지

            setGeocodingStats((prevStats) => ({
                ...prevStats,
                // geocodingProcessingTime: processingTimeInSeconds
            }));

            // 0.8초 지연 후 결과 페이지로 전환
            setTimeout(() => {
                setMapComplete(true);
            }, 800);
        };

        process();
    };

    const multiFullGeoCoding = async (data: string[][], indexs: number[]) => {
        if (!initMapData) return;

        const items = [];

        // skip 된 주소의 개수만큼 실제 인덱스에서 차감처리 해줘야 하므로 넣어줄 인덱스로 사용할 값 재생성

        // 251118 | smyun | multiFullGeoCoding 진행 중 주소가 아닌 행수는 SKIP 처리하면서 index 꼬임 발생.
        // 인덱스 매핑 테이블 이용> 맵 객체를 만들어 요청 index와 실제 엑셀 행 번호를 저장
        const indexMap: Record<number, number> = {};
        let itemIdx = 1;
        for (let i = 0; i < indexs.length; ++i) {
            const rowIndex = indexs[i];
            // 병합된 엑셀의 경우 첫번째 행 이외에는 데이터 주소가 존재하지 않으므로 SKIP 처리
            if (!data[rowIndex][addressFieldIdx] || data[rowIndex][addressFieldIdx].length <= 5) continue; // 존재하지 않거나, 주소 내용이 5글자 미만이면 사실상 오류이므로 스킵
            items.push({ index: itemIdx, query: data[rowIndex][addressFieldIdx] });
            indexMap[itemIdx] = rowIndex; // itemIdx → 실제 행번호 매핑
            itemIdx++;
        }

        const body: MultiFullGeocodingReqBody = { addresses: items };
        const response = await DataProvider.MultiFullGeocoding(body);
		if (!response.flag) return null;

        // Geocoding 결과 통계 집계
        const stats = aggregateGeocodingStats(response.result as GeocodingResultItem[]);

		const failedAddresses: string[] = [];

		if( stats.failCnt > 0) {
			const results = response.result as GeocodingResultItem[];
			const failedResults = getFailedGeocodingResults(results);

			const addresses = getFailedQueriesFromFailedResults(failedResults, body);
			failedAddresses.push(...addresses);
		}

        setGeocodingStats((prevStats) => {
            const newStats:GeocodingStats  = {
                totalCnt: prevStats.totalCnt + stats.totalCnt,
                exactCnt: prevStats.exactCnt + stats.exactCnt,
                similarCnt: prevStats.similarCnt + stats.similarCnt,
                failCnt: prevStats.failCnt + stats.failCnt,
                matchingRate: 0, // 임시값, 아래에서 재계산
                // geocodingProcessingTime: prevStats.geocodingProcessingTime // 기존 값 유지
				failedAddresses: prevStats.failedAddresses ?? [],
            };

			if (failedAddresses.length > 0) {
				newStats.failedAddresses = [...(newStats.failedAddresses ?? []),...failedAddresses,];
			}

            // 전체 매칭율 재계산 ( 전체 건수 대비 성공 개수 비율 )
            newStats.matchingRate = newStats.totalCnt > 0 ? Math.round(((newStats.exactCnt + newStats.similarCnt) / newStats.totalCnt) * 100) : 0;

            return newStats;
        });

        const result: Feature[] = [];

        for (const fetchResult of response.result) {
			// fetchResult가 null/undefined면 스킵
			if (!fetchResult) continue;
			
            const requestIndex = fetchResult.index;

			//index가 없거나 매핑이 안되면 스킵
			if (requestIndex == null) continue;

            const rowIndex = indexMap[requestIndex]; // 실제 행 번호
			if (rowIndex == null) continue;

            const resultData = fetchResult.data;

			// data/coords 없으면 스킵
			const coords = resultData?.coords;
			if (!Array.isArray(coords) || coords.length < 2) continue;


            // data[rowIndex]를 사용해 GeoJSON 특성 생성
            const properties: GeoJsonProperties = {};
            // A~Z 필드 매핑
            data[rowIndex].forEach((col, c_idx) => {
                properties[alphabet[c_idx]] = col;
            });

            // FIELDNAME
            const fieldName: { [key: string]: string } = {};
            data[0].forEach((col, c_idx) => {
                fieldName[alphabet[c_idx]] = col;
            });

            properties["FIELDNAME"] = JSON.stringify(fieldName);

            const feature: Feature = {
                type: "Feature",
                geometry: {
                    type: "Point",
                    coordinates: resultData.coords,
                } as Point,
                properties: properties,
            };

            result.push(feature);
        }

        return result;
    };

    const geoCoding = async (data: string[][], idx: number) => {
        if (!initMapData) return;

        // Geocoding API
        const address = data[idx][addressFieldIdx];
        const param: GeocodingQueryParams = {
            index: String(idx),
            query: address,
        };

        const response = await DataProvider.Geocoding(param);

        // console.log("test", response);
        if (!response.flag) return null;

        const result = response.result;
        const properties: GeoJsonProperties = {};

        // A~Z 필드 매핑
        data[idx].forEach((col, c_idx) => {
            properties[alphabet[c_idx]] = col;
        });

        // FIELDNAME
        const fieldName: { [key: string]: string } = {};
        data[0].forEach((col, c_idx) => {
            fieldName[alphabet[c_idx]] = col;
        });

        properties["FIELDNAME"] = JSON.stringify(fieldName);

        const feature: Feature = {
            type: "Feature",
            geometry: {
                type: "Point",
                coordinates: result.coords,
            } as Point,
            properties: properties,
        };

        return feature;
    };

    // const escapeJsonString = (value: string): string => {
    //     return value.replace(/\\/g, "\\\\").replace(/"/g, '\\"').replace(/\r/g, "\\r").replace(/\n/g, "\\n").replace(/\t/g, "\\t");
    // };

    const retry = () => {
        progress(failMapData);
    };

    const selectMarkerLabel = (option: number) => {
        if (initMapData) {
            setMarkerInfo((prev) => ({ ...prev, fieldNameIdx: option }));

            // 전체 contents 배열을 재구성
            const newContents: Content[] = [];

            // 1. 선택된 장소이름 필드 추가 (첫 번째)
            newContents.push({
                fieldName: initMapData[0][option],
                fieldValue: "",
                template: DataTemplate.none,
                style: { ...defaultStyle },
            });

            // 2. 나머지 필드들 추가 (선택된 필드 제외)
            for (let idx = 0; idx < Math.min(5, initMapData[0].length); idx++) {
                if (idx !== option && newContents.length < 5) {
                    newContents.push({
                        fieldName: initMapData[0][idx],
                        fieldValue: "${fieldValue}",
                        template: DataTemplate.none,
                        style: { ...defaultStyle },
                    });
                }
            }

            setMapSettingData((prev) => ({
                ...prev,
                placeInfo: {
                    ...prev.placeInfo,
                    title: initMapData[1][option],
                    contents: newContents,
                },
            }));
        }
    };

    const markerSettingFn = {
        setMarkerInfo,
        selectMarkerLabel,
    };

    // SummaryPrompt 관련 함수들
    const handleCloseSummaryPrompt = () => {
        setShowSummaryPrompt(false);
    };

    const handleSummaryComplete = (summary: string) => {
        setSummaryData(summary);
        setSummaryCompleted(true);
        // 요약이 완료되면 다음 단계로 진행할 수 있도록 알림
        console.log("AI 요약 완료:", summary);
    };

    const importData = (data: string[][]) => {
        setInitMapData(data);
        setIsData(!isData);
        setFailMapData([data[0]]);
    };

    const completeMapSetting = () => {
        setMapComplete(true);
    };

    const initialize = () => {
        setProcessIdx(0); // 지도 제작 기능 프로세스 인덱스 0~5
        setIsData(false); // 엑셀 파일에 주소 데이터 유무 파악
        setInitMapData(undefined); // 엑셀 기반 지도 정보
        setSampleData({ row1: [], row2: [] }); // 주소 추론 샘플 데이터
        setAddressFieldIdx(0); // 주소 필드 인덱스
        setAddressFieldMapdata(undefined); // 주소 필드 데이터
        setInferredAddressFieldIndices(undefined); // 추론된 주소필드 인덱스 리스트
        setSelectedAddressIdx(0); // 선택된 주소 인덱스(필드 인덱스 아님)
        setMainImage({ isMainImage: false, mainImageIdx: 0 });

        // 25.07.18 | Kim Young Jun | 중복 검사 상태 초기화
        setIsFieldConflict(false);
    };

    const onCompleteUserEdit = (text: string) => {
        setIsUserDataEditMode(false);
        const parsed = text.split("\n").map((line) => line.split(",").map((cell) => cell.trim()));
        setInitMapData(parsed);
    };

    const onCancleUserEdit = () => {
        setIsUserDataEditMode(false);
        if (initMapData) {
            setInitMapData(initMapData.map((row) => [...row]));
        }
    };

    const render = () => {
        if (!initMapData) return null;

        switch (processIdx) {
            // 0: 파일(Excel) 임포트 단계
            case 0:
                return (
                    addressFieldMapdata && (
                        <AddressFieldSetting
                            processIdx={processIdx}
                            canProceed={canProceed} // 25.07.18 | Kim Young Jun | 주소 필드 설정 단계에서 진행 가능 여부
                            setCanProceed={setCanProceed}
                            progressProcess={progressProcess}
                            AddressFieldMapData={addressFieldMapdata}
                            selectedAddressIdx={selectedAddressIdx}
                            setSelectedAddressIdx={setSelectedAddressIdx}
                        />
                    )
                );
            // 1: 마커 설정 단계
            case 1:
                return (
                    <MarkerSetting
                        processIdx={processIdx}
                        canProceed={canProceed}
                        setCanProceed={setCanProceed}
                        progressProcess={progressProcess}
                        sampleData={sampleData}
                        optionByCategory={optionByCategory}
                        markerInfo={markerInfo}
                        markerSettingFn={markerSettingFn}
                    />
                );
            // 2: 장소 정보 입력 단계
            case 2:
                return (
                    <PlaceInfoSetting
                        processIdx={processIdx}
                        progressProcess={progressProcess}
                        sampleData={sampleData}
                        mapSettingData={mapSettingData}
                        setMapSettingData={setMapSettingData}
                        mainImage={mainImage}
                        setMainImage={setMainImage}
                    />
                );
            // 3: 배경지도 선택 단계
            case 3:
                return <MapSetting processIdx={processIdx} progressProcess={progressProcess} mapType={mapType} setMapType={setMapType} />;
            // 4: 맵 정보 입력 완료 확인 단계
            case 4:
                return (
                    <MapSettingComplete
                        processIdx={processIdx}
                        progressProcess={progressProcess}
                        mapType={mapType}
                        completeMapSetting={completeMapSetting}
                        runGeocoding={runGeocoding}
                        mapComplete={mapComplete}
                        geoCodingProgress={geoCodingProgress}
                        geocodingStats={geocodingStats}
                        retry={retry}
                    />
                );
            // 5: 맵 정보 저장 단계
            case 5:
                if (!mapSaveData || !geojsonString) return null;
                return (
                    <MapComplete
                        progressProcess={progressProcess}
                        data={{ ...mapSettingData, marker: markerInfo, data: initMapData! }}
                        saveData={mapSaveData!}
                        geojson={geojsonString}
                        categories={createCategory()}
                        processIdx={processIdx}
                    />
                );
            default:
                return null;
        }
    };

    return (
        <>
            <ModalComponent />
            {initMapData && addressFieldMapdata && isData ? (
                <div ref={containerRef} className={styles.container}>
                    <div className={styles.contents_wrapper}>
                        <DataTable
                            mapData={initMapData}
                            inferredAddressFieldIndices={inferredAddressFieldIndices}
                            setMapData={setInitMapData}
                            isUserDataEditMode={isUserDataEditMode}
                            onCompleteUserEdit={onCompleteUserEdit}
                            onCancleUserEdit={onCancleUserEdit}
                        />

                        {processIdx === 0 && (
                            <SummaryPrompt
                                isVisible={showSummaryPrompt}
                                mapData={initMapData || []}
                                onClose={handleCloseSummaryPrompt}
                                onSummaryComplete={handleSummaryComplete}
                                initialSummary={summaryData}
                                initialCompleted={summaryCompleted}
                                processIdx={processIdx}
                            />
                        )}

                        <div className={`${styles.wrapPopup}`}>{render()}</div>
                        {failMapData.length > 1 && (
                            <DataTable
                                mapData={failMapData}
                                isFailMapData={true}
                                inferredAddressFieldIndices={inferredAddressFieldIndices}
                                setFailMapData={setFailMapData}
                                fieldIdx={addressFieldIdx}
                                isUserDataEditMode={isUserDataEditMode}
                                onCompleteUserEdit={onCompleteUserEdit}
                                onCancleUserEdit={onCancleUserEdit}
                            />
                        )}
                    </div>
                </div>
            ) : (
                <div className={styles.container} id={"create_map"}>
                    <div className={styles.contents_wrapper}>
                        <div className={styles.contents_container}>
                            <span className={styles.tit}>
                                직접 지도를 만들어보세요. <br />
                                위치 데이터를 포함하는 데이터를 붙여 넣으세요!
                            </span>
                            <CreateMapProcess />
                        </div>
                        <DataImporter importData={importData} />
                        <div className={styles.btnContainer}>
                            <Link to={path.GUIDE} style={{ textDecoration: "none" }}>
                                <div className={styles.detailsBtn}>
                                    <span className={styles.btnInner}>상세 가이드 보기</span>
                                </div>
                            </Link>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}

export default CreateMap;
